import { createSelector } from 'reselect';
import { RouteName } from '@mews-commander/enums';
import { getStringOrUndefinedProp, getStringProp } from '@mews/fp';
import { CommanderThunkAction } from '@mews-commander/types';
import { getResolveRoute } from '@mews-commander/legacy-app-instance';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { isCurrentEnterpriseGroupBillingEnabled } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import { isAccountIdCompany } from '@mews-commander/legacy-entities/src/selectors/bills';
import { openModal } from '../app/actions/common';

export const getBillingGroupUrl = createSelector(
    getResolveRoute,
    getStringOrUndefinedProp('groupId'),
    getStringOrUndefinedProp('accountId'),
    getStringOrUndefinedProp('id'),
    getStringOrUndefinedProp('orderItemId'),
    getCurrentEnterpriseId,
    (resolveRoute, groupId, accountId, billId, orderItemId, enterpriseId) =>
        resolveRoute(RouteName.BillingGroup, {
            groupId,
            query: {
                EnterpriseId: enterpriseId,
                ...(accountId && { AccountId: accountId }),
                ...(billId && { BillId: billId }),
                ...(orderItemId && { OrderItemId: orderItemId }),
            },
        })
);

export const getBillingAccountUrl = createSelector(
    getResolveRoute,
    getStringProp('accountId'),
    getStringOrUndefinedProp('id'),
    getStringOrUndefinedProp('orderItemId'),
    getCurrentEnterpriseId,
    isAccountIdCompany,
    (resolveRoute, accountId, billId, orderItemId, enterpriseId, isCompany) =>
        isCompany
            ? resolveRoute(RouteName.CompanyDetail, {
                  id: accountId,
                  query: {
                      tab: 'Billing',
                      ...(accountId && { AccountId: accountId }),
                      ...(billId && { BillId: billId }),
                      ...(orderItemId && { OrderItemId: orderItemId }),
                  },
              })
            : resolveRoute(RouteName.BillingAccount, {
                  accountId,
                  query: {
                      EnterpriseId: enterpriseId,
                      ...(accountId && { AccountId: accountId }),
                      ...(billId && { BillId: billId }),
                      ...(orderItemId && { OrderItemId: orderItemId }),
                  },
              })
);

export const getGroupOrAccountBillingUrl = createSelector(
    isCurrentEnterpriseGroupBillingEnabled,
    getBillingGroupUrl,
    getBillingAccountUrl,
    (groupBillingEnabled, groupUrl, accountUrl) => (groupBillingEnabled && groupUrl ? groupUrl : accountUrl)
);

export const openGroupBillingModal =
    ({ groupId }: { groupId: string }): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        if (isCurrentEnterpriseGroupBillingEnabled(state)) {
            const url = getBillingGroupUrl(state, { groupId });
            return dispatch(openModal(url));
        }
    };

export const openGroupOrAccountBillingModal =
    ({ groupId, accountId }: { groupId?: string; accountId: string; onClose?: () => void }): CommanderThunkAction =>
    (dispatch, getState) => {
        const url = getGroupOrAccountBillingUrl(getState(), { groupId, accountId });
        return dispatch(openModal(url));
    };
